import type { Editor } from "@tiptap/react";
import type { CollaborateUserProfile, CollaborateUserRef } from "../types/collaborateThread";

const SIGNOFF_TAG_RE = /<signoff(?:\s+id="([^"]*)")?\s*>@?([^<]*)<\/signoff>/i;
const SIGNOFF_TAG_GLOBAL_RE = /<signoff(?:\s+id="[^"]*")?\s*>[\s\S]*?<\/signoff>/gi;

export function parseSignoffIdFromBody(body: string): string | null {
  const m = body.match(SIGNOFF_TAG_RE);
  const id = m?.[1]?.trim();
  return id || null;
}

export function parseSignoffNameFromBody(body: string): string | null {
  const m = body.match(SIGNOFF_TAG_RE);
  if (!m) {
    return null;
  }
  const name = m[2].trim().replace(/^@/, "");
  return name || null;
}

export function resolveUserRefById(
  id: string,
  users: CollaborateUserProfile[],
): CollaborateUserRef | null {
  const user = users.find((u) => u.id === id);
  return user ? { id: user.id, name: user.name, email: user.email } : null;
}

export function resolveUserRefByName(
  name: string,
  users: CollaborateUserProfile[],
): CollaborateUserRef | null {
  const q = name.trim().toLowerCase();
  const user = users.find(
    (u) => u.name.toLowerCase() === q || u.name.toLowerCase().startsWith(q),
  );
  return user ? { id: user.id, name: user.name, email: user.email } : null;
}

export function parseSignoffAssigneeFromBody(
  body: string,
  users: CollaborateUserProfile[],
): CollaborateUserRef | null {
  const id = parseSignoffIdFromBody(body);
  if (id) {
    const byId = resolveUserRefById(id, users);
    if (byId) {
      return byId;
    }
  }
  const name = parseSignoffNameFromBody(body);
  if (!name) {
    return null;
  }
  return resolveUserRefByName(name, users);
}

/** Read sign-off assignee from the editor node attrs (authoritative at post time). */
export function getSignoffAssigneeFromEditor(
  editor: Editor,
  users: CollaborateUserProfile[],
): CollaborateUserRef | null {
  let assignee: CollaborateUserRef | null = null;
  editor.state.doc.descendants((node) => {
    if (assignee || node.type.name !== "signoff") {
      return;
    }
    const id = String(node.attrs.id ?? "").trim();
    if (id) {
      assignee = resolveUserRefById(id, users);
      return;
    }
    const label = String(node.attrs.label ?? "").trim();
    if (label) {
      assignee = resolveUserRefByName(label, users);
    }
  });
  return assignee;
}

/** Strip sign-off tags so @-mention parsing does not treat sign-off name as a mention. */
export function stripSignoffTags(body: string): string {
  return body.replace(SIGNOFF_TAG_GLOBAL_RE, "");
}
